import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCircleCheck,
  faFlask,
  faBan,
  faUsers,
  faCodeBranch,
  faHourglassHalf,
  faHeartPulse,
  faClipboardList,
  faRotateRight,
  faTriangleExclamation,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { platformDashboardService } from '@/services/platform-dashboard.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { cn } from '@/lib/cn';
import { platformAuditActionLabel } from './platform-audit-action-labels';
import type {
  PlatformAuditLogEntry,
  PlatformDashboardSummary,
  PlatformHealth,
  PlatformHealthServiceState,
  PlatformHealthStatus,
} from '@/types/platform-dashboard.types';

const APP_VERSION = import.meta.env.PACKAGE_VERSION;

/** Every section fails independently — one broken API must not blank the page. */
type SectionState = 'loading' | 'loaded' | 'error';

/** v1.1 §13 — a section that has not answered within 15 seconds is an error. */
const REQUEST_TIMEOUT_MS = 15_000;
const HEALTH_POLL_MS = 60_000;

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), REQUEST_TIMEOUT_MS),
    ),
  ]);
}

// ── KPI card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: number | string;
  icon: typeof faBuilding;
  iconColor: string;
  attention?: 'amber' | 'red';
  onClick: () => void;
}

function KpiCard({ label, value, icon, iconColor, attention, onClick }: KpiCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${value}`}
      className={cn(
        'flex w-full items-start gap-4 rounded-xl border bg-card p-5 text-left transition-colors',
        'hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        attention === 'red'
          ? 'border-destructive/40'
          : attention === 'amber'
            ? 'border-amber-500/40'
            : 'border-border',
      )}
    >
      <div
        className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', iconColor)}
      >
        <FontAwesomeIcon icon={icon} className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-2xl font-semibold text-foreground">{value}</p>
      </div>
    </button>
  );
}

function KpiSkeleton() {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-card p-5">
      <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-muted" />
      <div className="flex-1">
        <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-7 w-16 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="flex items-center justify-between border-b border-border py-2.5 last:border-0">
      <div className="h-3 w-32 animate-pulse rounded bg-muted" />
      <div className="h-3 w-16 animate-pulse rounded bg-muted" />
    </div>
  );
}

/**
 * Honest state for a section that is unavailable, empty, failed, or withheld by
 * permission. It never substitutes a value and never implies data exists.
 */
function SectionMessage({
  icon,
  message,
  onRetry,
}: {
  icon: typeof faBuilding;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
        <FontAwesomeIcon icon={icon} className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mt-3 max-w-xs text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ── Health ──────────────────────────────────────────────────────────────────

const HEALTH_ICON: Record<PlatformHealthStatus, typeof faCircleCheck> = {
  healthy: faCircleCheck,
  degraded: faTriangleExclamation,
  down: faCircleXmark,
};
const HEALTH_COLOR: Record<PlatformHealthStatus, string> = {
  healthy: 'text-green-600 dark:text-green-400',
  degraded: 'text-amber-500',
  down: 'text-destructive',
};
const HEALTH_TEXT: Record<PlatformHealthStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
};
const SERVICE_LABEL: Record<string, string> = {
  database: 'Database',
  redis: 'Redis',
  emailGateway: 'Email Gateway',
  queueSystem: 'Queue System',
  storage: 'Storage',
};

function HealthRow({ name, state }: { name: string; state: PlatformHealthServiceState }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2.5 last:border-0">
      <span className="text-sm text-foreground">{SERVICE_LABEL[name] ?? name}</span>
      <div className="flex items-center gap-1.5">
        {state.latencyMs !== undefined && (
          <span className="text-xs text-muted-foreground">{state.latencyMs}ms</span>
        )}
        <FontAwesomeIcon
          icon={HEALTH_ICON[state.status]}
          className={cn('h-3.5 w-3.5', HEALTH_COLOR[state.status])}
          aria-hidden="true"
        />
        {/* Status is always TEXT as well as colour — never colour alone. */}
        <span className={cn('text-xs font-medium', HEALTH_COLOR[state.status])}>
          {HEALTH_TEXT[state.status]}
        </span>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function PlatformDashboardPage() {
  const navigate = useNavigate();
  const role = usePlatformAuthStore((s) => s.user?.role);

  const canSystem = canPlatform(role, 'platform:system:view');
  const canAudit = canPlatform(role, 'platform:audit-log:view');

  const [summary, setSummary] = useState<PlatformDashboardSummary>({});
  const [summaryState, setSummaryState] = useState<SectionState>('loading');

  const [health, setHealth] = useState<PlatformHealth | null>(null);
  const [healthState, setHealthState] = useState<SectionState>('loading');

  const [activity, setActivity] = useState<PlatformAuditLogEntry[]>([]);
  const [activityState, setActivityState] = useState<SectionState>('loading');

  const loadSummary = useCallback(() => {
    setSummaryState('loading');
    withTimeout(platformDashboardService.getSummary())
      .then((data) => {
        setSummary(data);
        setSummaryState('loaded');
      })
      .catch(() => setSummaryState('error'));
  }, []);

  const loadHealth = useCallback((silent = false) => {
    if (!silent) setHealthState('loading');
    withTimeout(platformDashboardService.getHealth())
      .then((data) => {
        setHealth(data);
        setHealthState('loaded');
      })
      .catch(() => setHealthState('error'));
  }, []);

  const loadActivity = useCallback(() => {
    setActivityState('loading');
    withTimeout(platformDashboardService.getRecentActivity(10))
      .then((page) => {
        setActivity(Array.isArray(page.items) ? page.items : []);
        setActivityState('loaded');
      })
      .catch(() => setActivityState('error'));
  }, []);

  useEffect(() => loadSummary(), [loadSummary]);

  // Health is fetched and polled ONLY when the caller may read it. A role
  // without platform:system:view never issues the request, rather than issuing
  // it and discarding 403s.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!canSystem) return;
    loadHealth();
    pollRef.current = setInterval(() => loadHealth(true), HEALTH_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [canSystem, loadHealth]);

  useEffect(() => {
    if (!canAudit) return;
    loadActivity();
  }, [canAudit, loadActivity]);

  const goCompanies = (): void => navigate(PLATFORM_ROUTES.COMPANIES);
  const goUsers = (): void => navigate(PLATFORM_ROUTES.PLATFORM_USERS);

  // A card renders ONLY when the server actually sent its value. An absent field
  // means the backend withheld it — never rendered as 0, a dash, or a guess.
  const has = (v: number | undefined): v is number => typeof v === 'number';
  const anyKpi =
    has(summary.totalCompanies) ||
    has(summary.trialsExpiringSoon7d) ||
    has(summary.activePlatformUsers);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Dashboard</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Platform operations overview</p>
        </div>
        <button
          type="button"
          onClick={loadSummary}
          aria-label="Refresh dashboard"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <FontAwesomeIcon icon={faRotateRight} className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* KPI row — 1 col mobile, 2 col tablet, 3 col desktop (v1.1 §5.8) */}
      {summaryState === 'loading' ? (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="kpi-loading"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <KpiSkeleton key={i} />
          ))}
        </div>
      ) : summaryState === 'error' ? (
        <div className="rounded-xl border border-border bg-card" data-testid="kpi-error">
          <SectionMessage
            icon={faBuilding}
            message="Unable to load company data."
            onRetry={loadSummary}
          />
        </div>
      ) : !anyKpi ? (
        <div className="rounded-xl border border-border bg-card" data-testid="kpi-unavailable">
          <SectionMessage
            icon={faBuilding}
            message="Your role does not have access to dashboard metrics."
          />
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="kpi-grid"
        >
          {has(summary.totalCompanies) && (
            <KpiCard
              label="Total Companies"
              value={summary.totalCompanies}
              icon={faBuilding}
              iconColor="bg-primary/[0.12] text-primary"
              onClick={goCompanies}
            />
          )}
          {has(summary.activeCompanies) && (
            <KpiCard
              label="Active"
              value={summary.activeCompanies}
              icon={faCircleCheck}
              iconColor="bg-green-500/10 text-green-600 dark:text-green-400"
              onClick={goCompanies}
            />
          )}
          {has(summary.trialCompanies) && (
            <KpiCard
              label="On Trial"
              value={summary.trialCompanies}
              icon={faFlask}
              iconColor="bg-amber-500/10 text-amber-600"
              onClick={goCompanies}
            />
          )}
          {has(summary.trialsExpiringSoon7d) && (
            <KpiCard
              label="Expiring (7d)"
              value={summary.trialsExpiringSoon7d}
              icon={faHourglassHalf}
              iconColor="bg-amber-500/10 text-amber-600"
              attention={summary.trialsExpiringSoon7d > 0 ? 'amber' : undefined}
              onClick={goCompanies}
            />
          )}
          {has(summary.suspendedCompanies) && (
            <KpiCard
              label="Suspended"
              value={summary.suspendedCompanies}
              icon={faBan}
              iconColor="bg-destructive/10 text-destructive"
              attention={summary.suspendedCompanies > 0 ? 'red' : undefined}
              onClick={goCompanies}
            />
          )}
          {has(summary.activePlatformUsers) && (
            <KpiCard
              label="Platform Users"
              value={summary.activePlatformUsers}
              icon={faUsers}
              iconColor="bg-primary/[0.12] text-primary"
              onClick={goUsers}
            />
          )}
          <KpiCard
            label="Version"
            value={`v${APP_VERSION}`}
            icon={faCodeBranch}
            iconColor="bg-muted text-muted-foreground"
            onClick={goCompanies}
          />
        </div>
      )}

      {/* Health + Activity — stacked below 1024px, 50/50 above (v1.1 §5.8) */}
      {/*
       * `grid-cols-1` is REQUIRED below `lg`, not cosmetic (D-1, Phase 12.2H).
       *
       * Without it the single stacked column is an implicit `auto` track, whose
       * automatic minimum size is min-content. Recent Activity's min-content is
       * 451px — its row pairs a `shrink-0` timestamp with a `truncate` line
       * carrying a full 36-char resource UUID — so the track refused to shrink
       * below 451px inside 349px of available width, pushing 78px of overflow
       * into <main> and putting the health refresh button off-screen at 412px.
       *
       * `grid-cols-1` is `repeat(1, minmax(0, 1fr))`; the 0 floor lets the track
       * shrink to the container and lets the existing `min-w-0` / `truncate`
       * behave as intended. This matches the two KPI grids above, which already
       * declare `grid-cols-1` and never exhibited the defect.
       *
       * `lg:grid-cols-2` still wins at >=1024px — desktop layout is unchanged.
       */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div
          className="rounded-xl border border-border bg-card p-5"
          data-testid="platform-health-panel"
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Platform Health</h2>
              {health && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Checked {new Date(health.checkedAt).toLocaleTimeString()}
                </p>
              )}
            </div>
            {canSystem && (
              <button
                type="button"
                onClick={() => loadHealth()}
                aria-label="Refresh platform health"
                className="rounded-lg border border-border px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
              </button>
            )}
          </div>

          {!canSystem ? (
            <SectionMessage
              icon={faHeartPulse}
              message="Your role does not have access to platform health."
            />
          ) : healthState === 'loading' ? (
            <div data-testid="health-loading">
              {Array.from({ length: 5 }, (_, i) => (
                <RowSkeleton key={i} />
              ))}
            </div>
          ) : healthState === 'error' ? (
            <SectionMessage
              icon={faHeartPulse}
              message="Unable to reach health API."
              onRetry={() => loadHealth()}
            />
          ) : health ? (
            <div>
              {Object.entries(health.services).map(([name, state]) => (
                <HealthRow key={name} name={name} state={state} />
              ))}
              <p className="mt-3 text-xs font-medium text-muted-foreground">
                Overall:{' '}
                <span className={HEALTH_COLOR[health.overall]}>{HEALTH_TEXT[health.overall]}</span>
              </p>
            </div>
          ) : null}
        </div>

        <div
          className="rounded-xl border border-border bg-card p-5"
          data-testid="recent-activity-panel"
        >
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-foreground">Recent Activity</h2>
          </div>

          {!canAudit ? (
            <SectionMessage
              icon={faClipboardList}
              message="Your role does not have access to platform activity."
            />
          ) : activityState === 'loading' ? (
            <div data-testid="activity-loading">
              {Array.from({ length: 5 }, (_, i) => (
                <RowSkeleton key={i} />
              ))}
            </div>
          ) : activityState === 'error' ? (
            <SectionMessage
              icon={faClipboardList}
              message="Unable to load activity log."
              onRetry={loadActivity}
            />
          ) : activity.length === 0 ? (
            <SectionMessage icon={faClipboardList} message="No platform activity recorded yet." />
          ) : (
            <>
              <ul aria-label="Recent platform activity" className="list-none">
                {activity.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-start gap-3 border-b border-border py-2.5 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {platformAuditActionLabel(entry.action)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {entry.resourceType}
                        {entry.resourceId ? ` · ${entry.resourceId}` : ''}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{entry.actorEmail}</p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {new Date(entry.occurredAt).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => navigate(PLATFORM_ROUTES.OPERATIONS_AUDIT)}
                className="mt-3 text-xs font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                View All Activity →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
